import { Box, Tab, Tabs } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import TabPanel from '@/components/layouts/TabPanel'
import { BidHistoriesByTypeView } from '@/features/bidding/components/bidView/BidHistoriesByTypeView'
import { BidType } from '@/features/bidding/constants'
import { useHasCapacityBidUpdates, useHasEnergyBidUpdates } from '@/features/bidding/hooks/useHasBidUpdates'
import { buildBidViewLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import getGroupedBidParamsFromUrl from '@/features/bidding/utils/groupedBidParams/getGroupedBidParamsFromUrl'

type BidHistoriesViewContentProps = {
  shouldUpdateUrlWhenActiveTabChanges: boolean
}

function BidHistoriesViewContent({ shouldUpdateUrlWhenActiveTabChanges }: Readonly<BidHistoriesViewContentProps>) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const groupParams = getGroupedBidParamsFromUrl(location)
  const { isLoading: isLoadingCapacityBids, hasBidUpdates: hasCapacityBids } = useHasCapacityBidUpdates(groupParams)
  const { isLoading: isLoadingEnergyBids, hasBidUpdates: hasEnergyBids } = useHasEnergyBidUpdates(groupParams)
  const [activeTab, setActiveTab] = useState(-1)

  useEffect(() => {
    if (isLoadingCapacityBids || isLoadingEnergyBids) {
      return
    }

    let newActiveTab = -1

    if (hasCapacityBids && groupParams?.bidType === BidType.CAPACITY) {
      newActiveTab = 0
    } else if (hasEnergyBids && groupParams?.bidType === BidType.ENERGY) {
      newActiveTab = 1
    } else {
      newActiveTab = 0
    }

    setActiveTab(newActiveTab)
  }, [isLoadingCapacityBids, isLoadingEnergyBids, hasCapacityBids, hasEnergyBids, groupParams?.bidType])

  if (activeTab == -1 || isLoadingCapacityBids || isLoadingEnergyBids || !groupParams) {
    return <FullPageSpinner />
  }

  // Handle when there are no bids
  if (!hasCapacityBids && !hasEnergyBids) {
    return (
      <Box sx={{ padding: 2 }}>
        <p>{t('bidding.results.no_bids')}</p>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          scrollButtons="auto"
          style={{ minHeight: 0 }}
          value={activeTab}
          variant="scrollable"
          onChange={(_, newValue) => {
            setActiveTab(newValue)

            if (shouldUpdateUrlWhenActiveTabChanges) {
              navigate(
                buildBidViewLink({
                  deliveryDay: groupParams?.deliveryDay,
                  portfolioCode: groupParams?.portfolioCode,
                  marketProgram: groupParams?.marketProgram,
                  bidType: newValue === 0 ? BidType.CAPACITY : BidType.ENERGY,
                }),
              )
            }
          }}
        >
          {hasCapacityBids && <Tab label={t('bidding.results.capacity_bids')} style={{ minHeight: 0 }} value={0} />}
          {hasEnergyBids && <Tab label={t('bidding.results.energy_bids')} style={{ minHeight: 0 }} value={1} />}
        </Tabs>
      </Box>
      {hasCapacityBids && (
        <TabPanel index={0} value={activeTab}>
          <BidHistoriesByTypeView bidType={BidType.CAPACITY} />
        </TabPanel>
      )}
      {hasEnergyBids && (
        <TabPanel index={1} value={activeTab}>
          <BidHistoriesByTypeView bidType={BidType.ENERGY} />
        </TabPanel>
      )}
    </>
  )
}

export default BidHistoriesViewContent
